/**
 * Date Utils
 *
 * @since 2018-03-29
 * @author kdydesign.kim
 */

import Moment from 'moment'
import {i18n} from '../plugins/i18n'

/**
 * Moment format 변경
 * @param val
 * @returns {string}
 */
function getLocalFormat (val) {
  let localFormat = 'YYYYMMDDHHmmss'
  const localDateStr = val.replace(/[/\s:-]/g, '')

  if (localDateStr.length === 4) {
    localFormat = 'YYYY'
  }

  if (localDateStr.length === 6) {
    localFormat = 'YYYYMM'
  }

  if (localDateStr.length === 8) {
    localFormat = 'YYYYMMDD'
  }

  if (localDateStr.length === 10) {
    localFormat = 'YYYYMMDDHH'
  }

  if (localDateStr.length === 12) {
    localFormat = 'YYYYMMDDHHmm'
  }

  return localFormat
}

/**
 * Client 기준 현재 시간 조회
 *
 * @param format
 * @returns {string}
 * @constructor
 */
export function clientDate (format = 'YYYY-MM-DD HH:mm:ss') {
  return Moment().format(format)
}

/**
 * 날짜 형식 변경
 *
 * @param val
 * @param format
 * @returns {string}
 */
export function formatDate (val, format = 'YYYY-MM-DD HH:mm:ss', localFormat = false) {
  let value = val

  if (val === void 0 || val === 'undefined') {
    value = Moment().format(format)
  }

  if (localFormat) {
    return Moment(value, getLocalFormat(value)).format(format)
  } else {
    return Moment(value).format(format)
  }
}

/**
 * 특정 날짜 구하기
 *
 * @param val
 * @param format
 * @returns {string}
 */
export function getStepDate (val, format = 'YYYY-MM-DD HH:mm:ss', step = 0, type = 'days') {
  return Moment(val).add(step, type).format(format)
}

/**
 * Quasar DateTimePicker format
 * 퀘이사 DateTimePicker의 형태로 변경
 *
 * @param value
 * @returns {string}
 */
export function quasarBuildFormat (value) {
  return Moment().set(value).toISOString()
}

/**
 * 1일 ~ 31일 데이터 생성
 *
 * @returns {Array}
 */
export function getAllDays () {
  const resultDays = []

  for (let i = 1; i <= 31; i++) {
    resultDays.push(`${i}${i18n.t('cmn.day')}`)
  }

  return resultDays
}

/**
 * 1월 ~ 12월 데이터 생성
 *
 * @returns {Array}
 */
export function getAllMonth () {
  const resultMonth = []

  for (let i = 1; i <= 12; i++) {
    resultMonth.push(`${i}${i18n.tc('cmn.month', 0)}`)
  }

  return resultMonth
}

/**
 * 날짜 비교
 * curr > target
 *
 * @param curr
 * @param target
 * @returns {boolean}
 */
export function compareIsAfter (curr, target, format = 'YYYY-MM-DD') {
  return Moment(curr).isAfter(formatDate(String(target), format))
}

/**
 * 날짜 비교
 * prevTarget < curr < nextTarget
 *
 * @param curr
 * @param target
 * @returns {boolean}
 */
export function compareIsBeetween (curr, prevTarget, nextTarget, format = 'YYYY-MM-DD') {
  return Moment(curr).isBetween(formatDate(String(prevTarget), format), formatDate(String(nextTarget), format))
}

/**
 * 시간 차이
 *
 * @param startDateTime
 * @param startDateTime
 * @param type
 * @returns {number}
 */
export function dateTimeGap (startDateTime, endDateTime, type = 'seconds') {
  const diff = Moment.duration(Moment(startDateTime).diff(endDateTime))

  if (type === 'years') {
    return diff.asYears()
  }

  if (type === 'months') {
    return diff.asMonths()
  }

  if (type === 'days') {
    return diff.asDays()
  }

  if (type === 'weeks') {
    return diff.asWeeks()
  }

  if (type === 'hours') {
    return diff.asHours()
  }

  if (type === 'minutes') {
    return diff.asMinutes()
  }

  if (type === 'seconds') {
    return diff.asSeconds()
  }
}

// 남은 시간 표현
export function getRemainTimeStr (gap) {
  gap = gap / 1000
  const d = Math.floor(gap / 86400)
  const h = Math.floor((gap - d * 86400) / 3600 % 3600)
  const m = Math.floor((gap - h * 3600) / 60 % 60)
  const s = Math.floor((gap - m * 60) % 60)

  if (d !== 0) {
    return `${d}${i18n.t('cmn.day')} ${i18n.tc('cmn.timeParams', 0, {time: h})} ${i18n.tc('cmn.timeParams', 1, {time: m})} ${i18n.tc('cmn.timeParams', 2, {time: s})}`
  } else if (h !== 0) {
    return `${i18n.tc('cmn.timeParams', 0, {time: h})} ${i18n.tc('cmn.timeParams', 1, {time: m})} ${i18n.tc('cmn.timeParams', 2, {time: s})}`
  } else if (m !== 0) {
    return `${i18n.tc('cmn.timeParams', 1, {time: m})} ${i18n.tc('cmn.timeParams', 2, {time: s})}`
  }
  return `${i18n.tc('cmn.timeParams', 2, {time: s})}`
}

/**
 * 시간 객체
 * 년-월-일 시:분 데이터 가공
 *
 * @param value
 * @returns {{endHour: string, endMin: string, startMonth: number, startDay: string, startHour: string, endDay: string, startYear: string, startMin: string, endMonth: number, endYear: string}}
 */
export function getTimeObjDateTime (value, type = void 0) {
  const startYear = value.substring(0, 4)
  const startMonth = Number(value.substring(4, 6)) - 1
  const startDay = value.substring(6, 8)
  let startHour, startMin, endYear, endMonth, endDay, endHour, endMin

  if (type === 'fixedTime') {
    endYear = startYear
    endMonth = startMonth
    endDay = startDay
    startHour = '00'
    startMin = '00'
    endHour = '23'
    endMin = '59'
  } else {
    startHour = value.substring(8, 10)
    startMin = value.substring(10, 12)
    endYear = value.substring(12, 16)
    endMonth = Number(value.substring(16, 18)) - 1
    endDay = value.substring(18, 20)
    endHour = value.substring(20, 22)
    endMin = value.substring(22, 24)
  }

  const sd = quasarBuildFormat({
    year: startYear, month: startMonth, date: startDay, hour: startHour, minute: startMin
  })

  const ed = quasarBuildFormat({
    year: endYear, month: endMonth, date: endDay, hour: endHour, minute: endMin
  })

  return {
    startDate: formatDate(sd, 'YYYY-MM-DD HH:mm'),
    endDate: formatDate(ed, 'YYYY-MM-DD HH:mm')
  }
}

/**
 * 이름: getConvertUtc
 * 설명: Date 타입의 객체를 UTC 타입으로 변환하여 리턴한다.
 */
export function getConvertUtc (date) {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds())
}
